export const DEALS_FACET_ATTRIBUTE = 'deals';
export const DEALS_FACET_LABEL = 'Deals';

export type DealsFacetRefinementItem = { value: string; label?: string; isRefined?: boolean };

type DealsFacetOrderingValues = {
  order?: string[];
  hide?: string[];
  sortRemainingBy?: 'count' | 'alpha' | 'hidden';
};

type DealsFacetTransformResults = {
  renderingContent?: {
    facetOrdering?: {
      values?: Record<string, DealsFacetOrderingValues>;
    };
  };
};

const DEALS_FACET_DISPLAY_LABELS: Record<string, string> = {
  'Limited Time Offers': 'Limited-Time Offers',
  BUY_MORE_SAVE_MORE: 'Buy More Save More',
};

export const formatDealsFacetDisplayLabel = (facetValue: string): string =>
  DEALS_FACET_DISPLAY_LABELS[facetValue] ?? facetValue;

/** Capture Facet Display order on first sight so values do not jump when selected. */
export const registerDealsFacetDisplayOrder = (
  stableOrderMap: Map<string, number>,
  items: DealsFacetRefinementItem[],
): void => {
  items.forEach((item) => {
    if (!stableOrderMap.has(item.value)) {
      stableOrderMap.set(item.value, stableOrderMap.size);
    }
  });
};

/** Preserve Algolia Facet Display order (no hard-coded pin/hide of values). */
export const sortDealsFacetItems = <T extends DealsFacetRefinementItem>(
  items: T[],
  stableOrderMap?: Map<string, number>,
): T[] => {
  if (!items?.length) {
    return items ?? [];
  }

  if (!stableOrderMap) {
    return [...items];
  }

  return [...items].sort(
    (a, b) =>
      (stableOrderMap.get(a.value) ?? Number.MAX_SAFE_INTEGER) -
      (stableOrderMap.get(b.value) ?? Number.MAX_SAFE_INTEGER),
  );
};

/** Respect Facet Display "Hide facet values" from renderingContent. */
export const filterDealsFacetItemsByFacetOrderingHide = <T extends DealsFacetRefinementItem>(
  items: T[],
  hideValues?: string[] | null,
): T[] => {
  if (!hideValues?.length || !items?.length) {
    return items ?? [];
  }

  const hideSet = new Set(hideValues);
  return items.filter((item) => !hideSet.has(item.value));
};

export const getDealsFacetOrderingHideValues = (results?: DealsFacetTransformResults): string[] =>
  results?.renderingContent?.facetOrdering?.values?.[DEALS_FACET_ATTRIBUTE]?.hide ?? [];

/** Client-side Deals facet search (matches display + indexed labels; does not refine results). */
export const filterDealsFacetItemsBySearchTerm = <T extends DealsFacetRefinementItem>(
  items: T[],
  searchTerm: string,
): T[] => {
  const normalizedTerm = searchTerm.trim().toLowerCase();
  if (!normalizedTerm || !items?.length) {
    return items ?? [];
  }

  return items.filter((item) => {
    const displayLabel = formatDealsFacetDisplayLabel(item.label ?? item.value);
    const searchableValues = [item.value, item.label, displayLabel]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    return searchableValues?.some((value) => value?.split(/\s+/)?.some((word) => word?.startsWith(normalizedTerm)));
  });
};

export const prepareDealsFacetVisibleItems = <T extends DealsFacetRefinementItem>(
  items: T[],
  stableOrderMap: Map<string, number>,
  searchTerm = '',
  hideValues?: string[] | null,
): T[] => {
  const visibleItems = filterDealsFacetItemsByFacetOrderingHide(items, hideValues);
  registerDealsFacetDisplayOrder(stableOrderMap, visibleItems);
  const filteredItems = searchTerm.trim() ? filterDealsFacetItemsBySearchTerm(visibleItems, searchTerm) : visibleItems;
  return sortDealsFacetItems(filteredItems, stableOrderMap);
};

/**
 * Do not pass custom sortBy — InstantSearch only applies Facet Display (pin + sortRemainingBy)
 * when sortBy is the widget default. Hide is applied here because older helpers omit it.
 */
export const getDealsFacetRefinementListOptions = (
  stableOrderMapRef: { current: Map<string, number> },
  hideValuesRef?: { current: string[] },
) => ({
  transformItems: <T extends DealsFacetRefinementItem>(
    items: T[],
    { results }: { results?: DealsFacetTransformResults } = {},
  ) => {
    const hideValues = getDealsFacetOrderingHideValues(results);
    if (hideValuesRef) {
      hideValuesRef.current = hideValues;
    }
    const visibleItems = filterDealsFacetItemsByFacetOrderingHide(items, hideValues);
    registerDealsFacetDisplayOrder(stableOrderMapRef.current, visibleItems);
    return sortDealsFacetItems(visibleItems, stableOrderMapRef.current);
  },
});

export const isSortTransitionInProgress = (): boolean =>
  typeof window !== 'undefined' && sessionStorage.getItem('manualSortChange') === 'true';

/** Avoid switching to JSON facet fallback when DynamicWidgets briefly returns []. */
export const resolveStableDynamicWidgetAttributes = (
  attributesToRender: string[] | undefined,
  lastStableAttributes: string[],
): { attributes: string[]; lastStableAttributes: string[] } => {
  if (attributesToRender?.length) {
    return { attributes: attributesToRender, lastStableAttributes: attributesToRender };
  }
  return { attributes: lastStableAttributes, lastStableAttributes };
};

export const isDealsFacetAttribute = (attribute: string | undefined): boolean => attribute === DEALS_FACET_ATTRIBUTE;

export const getFacetItemDisplayLabel = (attribute: string | undefined, label: string): string =>
  isDealsFacetAttribute(attribute) ? formatDealsFacetDisplayLabel(label) : label;

/** Figma: always show in-facet search for Deals; other facets keep the 8+ value threshold. */
export const shouldShowFacetSearchBar = (
  attribute: string | undefined,
  initialItemCount: number | null,
  threshold = 8,
): boolean => isDealsFacetAttribute(attribute) || (initialItemCount !== null && initialItemCount >= threshold);

export const shouldHideFacetCounts = (attribute: string | undefined): boolean => isDealsFacetAttribute(attribute);

/**
 * Pass-through for facet attribute lists.
 * Deals must only appear when Facet Display / configured facet JSON includes it —
 * do not inject `deals` onto pages where it is not configured.
 */
export const mergeDealsFacetIntoAttributes = (attributes: string[] | undefined): string[] => attributes ?? [];
